/**
 * Counter vocabulary — shared tag-rendering specs for the per-section
 * SpineTagStrip and the run-level EventLegendStrip.
 *
 * Spec 23 P7. Previously ``TAG_SPECS`` was private to SpineTagStrip;
 * the event legend would have needed its own copy. Lifting the vocab
 * here means both surfaces read from ONE table — the priority order,
 * glyphs, colors, and aria labels stay in sync without a cross-file
 * mirror.
 *
 * SRP: pure data + tiny helpers. No React, no DOM, no state.
 *
 * OCP: adding a new EventKind that contributes to a counter is one
 * row in ``COUNTER_TAG_SPECS`` here, plus the matching counter on
 * SpineCounters and its bump in ``updateCounters`` (eventLog.ts).
 * No component code changes.
 */

import type { SpineCounters } from "./eventLog";
import { brandColors, kindColors, statusColors } from "./tokens";

// ---------------------------------------------------------------------------
// Shared spec table
// ---------------------------------------------------------------------------

export interface CounterTagSpec {
  /** The counter field this spec reads. */
  field: keyof SpineCounters;
  /** Label prefix before the count (e.g. "regen"). Empty when the
   *  label is glyph-only ("🌐", "📎"). */
  prefix: string;
  /** Optional leading glyph for emoji-style pills. */
  glyph?: string;
  /** Accent color — used for text + border tint. */
  color: string;
  /** Tooltip noun (plural — callers compose the full string). */
  aria: string;
}

/**
 * Priority-ordered tag specs. Ordering is attention-directing, not
 * magnitude-driven (see SpineTagStrip's DECISION record). First entry
 * renders first / leftmost; callers clip via ``maxVisible`` to manage
 * pane-width density.
 */
export const COUNTER_TAG_SPECS: readonly CounterTagSpec[] = [
  {
    field: "regens",
    prefix: "regen",
    color: statusColors.warn,
    aria: "regenerations",
  },
  {
    field: "webSearches",
    prefix: "",
    glyph: "🌐",
    color: kindColors.web,
    aria: "web searches",
  },
  {
    field: "citationSwaps",
    prefix: "cite",
    color: brandColors.primary,
    aria: "citation swaps",
  },
  { field: "askWhys", prefix: "Q", color: brandColors.mid, aria: "ask-whys" },
  {
    field: "wordEdits",
    prefix: "Word",
    glyph: "📎",
    color: kindColors.wordEdit,
    aria: "Word edits",
  },
  {
    field: "librarySwaps",
    prefix: "",
    glyph: "📚",
    color: statusColors.warn,
    aria: "library changes",
  },
];

// ---------------------------------------------------------------------------
// Pure helpers — sum + aggregate
// ---------------------------------------------------------------------------

/** Sum every counter field on a single section's record. */
export function totalTagCount(c: SpineCounters): number {
  return c.regens + c.webSearches + c.citationSwaps + c.askWhys + c.wordEdits + c.librarySwaps;
}

/** Zeroed counter record — used both as a default and as the
 *  accumulator seed for ``aggregateRunCounters``. */
export function zeroCounters(): SpineCounters {
  return {
    webSearches: 0,
    askWhys: 0,
    librarySwaps: 0,
    regens: 0,
    citationSwaps: 0,
    wordEdits: 0,
  };
}

/**
 * Fold every per-section counters into a single run-level record.
 * The event legend strip uses this to show "4 Q · 2 regen · 3 web · …"
 * at the top of the spine.
 *
 * Pure function over an immutable map input. O(sections + kinds) —
 * cheap even on a 38-module CSR after hundreds of events.
 */
export function aggregateRunCounters(counters: ReadonlyMap<string, SpineCounters>): SpineCounters {
  const acc = zeroCounters();
  for (const c of counters.values()) {
    acc.webSearches += c.webSearches;
    acc.askWhys += c.askWhys;
    acc.librarySwaps += c.librarySwaps;
    acc.regens += c.regens;
    acc.citationSwaps += c.citationSwaps;
    acc.wordEdits += c.wordEdits;
  }
  return acc;
}

/**
 * Priority-ordered list of tag specs with positive counts under the
 * given counters. Used by both SpineTagStrip (per-section) and
 * EventLegendStrip (run-level) so the visual order matches.
 *
 * Callers may clip with ``maxVisible`` to get an overflow count.
 */
/**
 * Default max-visible count — matches the per-row budget documented
 * in ``SpineTagStrip``. The event-legend strip overrides to a larger
 * value (run-level has more space) via the second argument.
 */
export const DEFAULT_MAX_VISIBLE_TAGS = 3;

export function selectVisibleTags(
  c: SpineCounters,
  maxVisible: number = DEFAULT_MAX_VISIBLE_TAGS,
): { visible: CounterTagSpec[]; overflowCount: number } {
  const active = COUNTER_TAG_SPECS.filter((spec) => c[spec.field] > 0);
  if (active.length <= maxVisible) {
    return { visible: active, overflowCount: 0 };
  }
  return {
    visible: active.slice(0, maxVisible),
    overflowCount: active.length - maxVisible,
  };
}

/**
 * Human-readable breakdown — lists only non-zero kinds, priority order.
 * Empty string when every counter is zero (caller shouldn't render).
 */
export function describeTagCounts(c: SpineCounters): string {
  const parts: string[] = [];
  if (c.regens > 0) parts.push(`${c.regens} regen${c.regens === 1 ? "" : "s"}`);
  if (c.webSearches > 0) parts.push(`${c.webSearches} web search${c.webSearches === 1 ? "" : "es"}`);
  if (c.citationSwaps > 0) parts.push(`${c.citationSwaps} citation swap${c.citationSwaps === 1 ? "" : "s"}`);
  if (c.askWhys > 0) parts.push(`${c.askWhys} ask-why${c.askWhys === 1 ? "" : "s"}`);
  if (c.wordEdits > 0) parts.push(`${c.wordEdits} Word edit${c.wordEdits === 1 ? "" : "s"}`);
  if (c.librarySwaps > 0) parts.push(`${c.librarySwaps} library change${c.librarySwaps === 1 ? "" : "s"}`);
  if (parts.length === 0) return "";
  const total = totalTagCount(c);
  return `${total} event${total === 1 ? "" : "s"}: ${parts.join(", ")}`;
}
